
import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileInput } from '@/components/ui/file-input';
import { RecordFormData, recordTypeOptions } from './types';
import DateInputField from './DateInputField';

interface RecordFormFieldsProps {
  formData: RecordFormData;
  setFormData: React.Dispatch<React.SetStateAction<RecordFormData>>;
  files: FileList | null;
  setFiles: React.Dispatch<React.SetStateAction<FileList | null>>;
  allowPrescriptions?: boolean;
}

export default function RecordFormFields({ 
  formData, 
  setFormData, 
  files, 
  setFiles,
  allowPrescriptions = true
}: RecordFormFieldsProps) {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date: string | Date) => {
    if (typeof date === 'string') {
      const newDate = new Date(date);
      if (!isNaN(newDate.getTime())) {
        setFormData(prev => ({ ...prev, record_date: newDate }));
      }
    } else if (date instanceof Date) {
      setFormData(prev => ({ ...prev, record_date: date }));
    }
  };

  const handleSelectChange = (value: string) => {
    setFormData(prev => ({ ...prev, record_type: value }));
  };

  const handleFileChange = (selectedFiles: FileList | null) => {
    setFiles(selectedFiles);
  };

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="title">Title*</Label>
        <Input
          id="title"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          placeholder="e.g., Annual Checkup, Surgery"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="record_type">Record Type*</Label>
        <Select 
          value={formData.record_type} 
          onValueChange={handleSelectChange}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select record type" />
          </SelectTrigger>
          <SelectContent>
            {recordTypeOptions.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DateInputField
        selectedDate={formData.record_date}
        onDateChange={handleDateChange}
      />

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          placeholder="Details about the medical condition, treatment, etc."
          rows={4}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="vet_name">Veterinarian Name</Label>
        <Input
          id="vet_name"
          name="vet_name"
          value={formData.vet_name}
          onChange={handleInputChange}
          placeholder="Dr. Smith"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="attachments">Attachments</Label>
        <FileInput 
          id="attachments"
          accept=".pdf,.jpg,.jpeg,.png"
          multiple
          onFileChange={handleFileChange}
          disabled={!allowPrescriptions}
        />
        <p className="text-xs text-muted-foreground">
          {allowPrescriptions 
            ? "Upload images or PDF files related to this medical record (optional)"
            : "Upgrade to Standard or higher to upload prescription documents"}
        </p>
      </div>
    </>
  );
}
